import { formatNumber } from '@shared/format';
import type { BadgeKind } from '@shared/ui/StateBadge';

/**
 * The vocabularies these four surfaces render, and the readings they perform.
 *
 * **No vocabulary member is a decision taken here.** The publish states are the
 * owning service's own machine, the content types are the `CONTENT_TYPES`
 * setting, and every resolver **falls through to the literal value with the
 * neutral badge** rather than dropping a row or crashing: the badge taxonomy is
 * closed while a configured vocabulary is not.
 *
 * The labels are this console's — the wire carries a machine token, and every
 * shaping decision stays on the reading side of the hop.
 */

// ---- the publish machine ---------------------------------------------------

/**
 * Three states, because three occur.
 *
 * `PUBLISHED` takes success. `DRAFT` takes neutral. `UNPUBLISHED` takes
 * **danger**: unlike an expired subscription, a title leaving the catalogue is a
 * removal from public view an operator caused, and the mockup marks it as one.
 *
 * The three review states are absent because `REVIEW_REQUIRED` is `false`: a
 * submission transitions straight to published, so nothing ever enters them and
 * a chip reading zero would announce a state that does not occur. The scheduled
 * state is absent for a different reason — it is **withdrawn**: the coming-soon
 * page is not a v1 surface, and `backend-content` has removed the member, its
 * column and its route.
 */
const PUBLISH_STATES: Readonly<Record<string, { label: string; kind: BadgeKind }>> = {
  PUBLISHED: { label: 'منشور', kind: 'success' },
  DRAFT: { label: 'مسوّدة', kind: 'neutral' },
  UNPUBLISHED: { label: 'غير منشور', kind: 'danger' },
};

export function resolvePublishState(state: string): { label: string; kind: BadgeKind } {
  return PUBLISH_STATES[state] ?? { label: state, kind: 'neutral' };
}

/** The facet's chip set, in the order the mockup declares it. */
export const PUBLISH_STATE_ORDER = ['PUBLISHED', 'DRAFT', 'UNPUBLISHED'] as const;

/**
 * What the row menu offers, derived from the row's own state.
 *
 * **The menu is derived, never authored per row.** A menu offering «انشر» on a
 * title whose badge two columns away reads «منشور» describes a state the
 * operator can see is false. And the wording follows the state as well as the
 * availability: «أعد النشر» on an unpublished title says something «انشر الآن»
 * does not.
 *
 * There is no scheduling entry any more. It is not hidden behind a flag — the
 * state it drove is gone from the machine, and an entry whose only outcome is a
 * `409` is worse than no entry.
 */
export interface RowActionShape {
  publish: 'publish' | 'republish' | null;
  unpublish: boolean;
}

export function resolveRowActions(state: string): RowActionShape {
  switch (state) {
    case 'PUBLISHED':
      return { publish: null, unpublish: true };
    case 'UNPUBLISHED':
      return { publish: 'republish', unpublish: false };
    default:
      // `DRAFT`, and any state this console has not been taught: offering the
      // forward transition is the honest default, and the service refuses what
      // its own machine does not permit.
      return { publish: 'publish', unpublish: false };
  }
}

// ---- content types ---------------------------------------------------------

const CONTENT_TYPES: Readonly<Record<string, string>> = {
  film: 'فيلم',
  series: 'مسلسل',
};

export function resolveContentType(contentType: string): string {
  return CONTENT_TYPES[contentType] ?? contentType;
}

/**
 * Whether this type partitions its assets into seasons.
 *
 * `GROUP_SEMANTICS` is `none` for a film and `season` for a series, so the
 * assets surface draws one shape or the other from the record's own type. **An
 * operator never converts one into the other from that surface** — the type is
 * an authoring decision.
 */
export function hasSeasons(contentType: string): boolean {
  return contentType === 'series';
}

// ---- artwork ---------------------------------------------------------------

const ARTWORK_PURPOSES: Readonly<Record<string, string>> = {
  THUMBNAIL: 'مصغّرة',
  HERO: 'صدارة',
  SLIDER: 'عارض',
  BACKGROUND: 'خلفية',
};

export function resolveArtworkPurpose(purpose: string): string {
  return ARTWORK_PURPOSES[purpose] ?? purpose;
}

/**
 * Where a purpose's image came from.
 *
 * The thumbnail is the only required purpose; the other three derive from it by
 * proportional crop or arrive through the one-action provider fill. The table
 * exists so the operator sees what **one** upload produces.
 */
const ARTWORK_SOURCES: Readonly<Record<string, { label: string; kind: BadgeKind }>> = {
  UPLOADED: { label: 'مرفوعة', kind: 'success' },
  PROVIDER: { label: 'من تعبئة المزوّد', kind: 'success' },
  DERIVED: { label: 'مشتقّة بقصٍّ متناسب', kind: 'neutral' },
  MISSING: { label: 'مطلوبة — تُرفَع', kind: 'warning' },
};

export function resolveArtworkSource(source: string): { label: string; kind: BadgeKind } {
  return ARTWORK_SOURCES[source] ?? { label: source, kind: 'neutral' };
}

// ---- renditions ------------------------------------------------------------

/**
 * A rung's state. **A size is not known for a file that does not exist yet**, so
 * an encoding rung renders its state and no figure rather than a zero.
 */
export function resolveRenditionState(state: string): { label: string; kind: BadgeKind } {
  // **One vocabulary for both tables.** The ladder and the encode job sit one
  // above the other on the same disclosure, and they were translating the same
  // word two ways: a rung the encoder passed over read «تُخُطِّيَت» in the job
  // block and raw `SKIPPED` in the ladder directly above it — one English token
  // on an Arabic-only surface, printed beside its own translation.
  return resolveEncodeOutcome(state);
}

// ---- encode jobs -----------------------------------------------------------

/**
 * The job's own state, and the outcome it records per requested rung.
 *
 * One map for both, because the encoder states the same vocabulary in both
 * places — a rung that failed and a job that failed read `FAILED` — and two maps
 * would be two chances to translate one word differently.
 */
const ENCODE_OUTCOMES: Readonly<Record<string, { label: string; kind: BadgeKind }>> = {
  QUEUED: { label: 'في الطابور', kind: 'neutral' },
  RUNNING: { label: 'قيد التنفيذ', kind: 'warning' },
  ENCODING: { label: 'قيد الترميز', kind: 'warning' },
  SUCCEEDED: { label: 'اكتملت', kind: 'success' },
  READY: { label: 'جاهزة', kind: 'success' },
  SKIPPED: { label: 'تُخُطِّيَت', kind: 'neutral' },
  FAILED: { label: 'تعذّر الترميز', kind: 'danger' },
};

export function resolveEncodeOutcome(value: string): { label: string; kind: BadgeKind } {
  return ENCODE_OUTCOMES[value] ?? { label: value, kind: 'neutral' };
}

// ---- asset readiness -------------------------------------------------------

/**
 * One asset's readiness, as a person waiting on it reads it.
 *
 * **The two states are not one.** `storage_state` says whether the bytes ever
 * landed and `encode_state` says whether anything was made from them, and a row
 * can sit terminally in the first while the second still reads `PENDING` — an
 * upload the browser gave up on leaves exactly that pair and no error anywhere
 * on screen. Storage is read first for that reason: a file that never arrived
 * is not «قيد التحويل», it is a file to upload again.
 *
 * `VALIDATED` + `READY` returns `null` rather than a badge. A ready file is the
 * ordinary case, and a mark on every ordinary row is a mark nobody reads.
 */
export function resolveAssetReadiness(
  storageState: string | null,
  encodeState: string | null,
): { label: string; kind: BadgeKind } | null {
  if (storageState === 'PENDING_UPLOAD') return { label: 'لم يكتمل الرفع', kind: 'warning' };
  if (storageState === 'QUARANTINED') return { label: 'قيد الفحص', kind: 'neutral' };
  if (storageState === 'REJECTED') return { label: 'رُفض الملفّ', kind: 'danger' };

  if (encodeState === 'FAILED') return { label: 'تعذّر التحويل', kind: 'danger' };
  if (encodeState === 'PENDING' || encodeState === 'RUNNING')
    return { label: 'جارٍ التحويل', kind: 'warning' };

  return null;
}

// ---- credit roles ----------------------------------------------------------

/**
 * `CREDIT_ROLES` — the permitted vocabulary. The **members** are read at runtime
 * from the owning service's configuration; this map only carries their wording,
 * and a member with no entry renders as its own token rather than being dropped.
 */
const CREDIT_ROLES: Readonly<Record<string, string>> = {
  director: 'إخراج',
  producer: 'إنتاج',
  writer: 'كتابة',
  actor: 'تمثيل',
  composer: 'تأليف موسيقي',
  cinematographer: 'تصوير',
};

export function resolveCreditRole(role: string): string {
  return CREDIT_ROLES[role] ?? role;
}

// ---- readings --------------------------------------------------------------

/**
 * A duration, as an operator reads it.
 *
 * Hours only where there are hours: `44:12` for an episode, `2:22:00` for a
 * feature. The separator is the colon the mono face already carries, and the
 * figures stay ASCII — see the note on the return below, and the timecodes the
 * asset and edit mockups draw.
 */
export function formatDuration(durationMs: number | null): string | null {
  // `Number.isFinite` rather than a null check alone: a value the wire did not
  // carry arrives as `undefined`, and `Math.round(undefined / 1000)` is `NaN` —
  // which this function used to render as the timecode «NaN:NaN».
  if (durationMs === null || !Number.isFinite(durationMs) || durationMs < 0) return null;

  const totalSeconds = Math.round(durationMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad = (value: number) => String(value).padStart(2, '0');

  // The timecode is an identifier rather than a quantity — it renders in the
  // mono face, ASCII, isolated, exactly as the mockup draws it.
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}

/**
 * A file size, as an operator reads it.
 *
 * The unit word is Arabic and the figure is Arabic-Indic through the shared
 * formatter — a size is a quantity a person reads, not a string a service
 * parses.
 */
const SIZE_UNITS = ['بايت', 'ك.ب', 'م.ب', 'غ.ب'] as const;

export function formatFileSize(sizeBytes: number | null): string | null {
  if (sizeBytes === null || sizeBytes < 0) return null;

  let value = sizeBytes;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < SIZE_UNITS.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const rounded = unitIndex === 0 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${formatNumber(rounded)} ${SIZE_UNITS[unitIndex]}`;
}

/**
 * The season / episode counts under a title's name in the list.
 *
 * A film draws its runtime instead, because it has no seasons to count — and
 * counting to «٠ مواسم» on a film would draw the synthetic parent the domain
 * model forbids.
 */
export function formatSeasonCount(count: number): string {
  // **Zero is an absence, not a quantity.** «٠ موسماً» is the accusative
  // singular Arabic uses after eleven and above — grammatically wrong for
  // nothing, and worse, it states a count for a series that has not been given
  // its first season yet. A series with no seasons has none; it does not have
  // zero of them.
  if (count === 0) return 'لا مواسم بعد';
  if (count === 1) return 'موسم واحد';
  if (count === 2) return 'موسمان';
  if (count >= 3 && count <= 10) return `${formatNumber(count)} مواسم`;
  return `${formatNumber(count)} موسماً`;
}

/**
 * A **film's** own files, counted in the noun a film has.
 *
 * A film has zero groups by construction, so it has no episodes — and the shape
 * card counted its single upload as «حلقة واحدة», which reads as a series with
 * one episode and is the synthetic season the domain model forbids, arrived at
 * from the other direction. What a film holds is a file.
 */
export function formatFilmAssetCount(count: number): string {
  if (count === 0) return 'لا ملفّات بعد';
  if (count === 1) return 'ملفّ واحد';
  if (count === 2) return 'ملفّان';
  if (count >= 3 && count <= 10) return `${formatNumber(count)} ملفّات`;
  return `${formatNumber(count)} ملفّاً`;
}

export function formatEpisodeCount(count: number): string {
  if (count === 0) return 'لا حلقات بعد';
  if (count === 1) return 'حلقة واحدة';
  if (count === 2) return 'حلقتان';
  if (count >= 3 && count <= 10) return `${formatNumber(count)} حلقات`;
  return `${formatNumber(count)} حلقة`;
}
